import { useCallback, useState, type MouseEvent } from "react"
import { RestartAlt, SaveAs, Tune } from "@mui/icons-material"
import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material"
import { dashboardEvents, DASHBOARD_EVENTS } from "helpers/events/dashboard-events"
import { isUuid } from "helpers/types/uuid"
import { useIsLayoutAccountShareEnabled } from "resources/account"
import { useMountedContentId } from "../../context/mounted-content"
import { useArrangementGetter, useSnapshotUnavailableReason } from "../../hooks/use-arrangement-getter"
import { useBackingLayoutConfig } from "../../hooks/use-backing-layout-config"
import { useCanManageLayouts } from "../../hooks/use-can-manage-layouts"
import { useSaveAsNewLayout } from "../../hooks/use-save-as-new-layout"
import ResetLayoutConfirmDialog from "./reset-layout-confirm-dialog"
import SaveAsNewDialog from "./save-as-new-dialog"

const RESET_NEEDS_PANELS_REASON = "Available once the dashboard's panels are showing"
const RESET_LAYOUT_PENDING_REASON = "Available once this dashboard's layout loads"

// Per-layout actions (Save as new, Reset) under one quiet icon trigger, set apart from the
// create/switch controls so managing the layout doesn't compete with them. [APP-3271]
export default function LayoutActionsMenu({ dashboardId }: { dashboardId: string }) {
  const getArrangement = useArrangementGetter()
  const saveAsNewUnavailableReason = useSnapshotUnavailableReason(dashboardId)
  // "Save as new" mints a layout (superuser, or an admin with layout_account_share on); "Reset" is a view action for everyone. [APP-3284]
  const canManageLayouts = useCanManageLayouts()
  const isLayoutAccountShareEnabled = useIsLayoutAccountShareEnabled()
  const canShareWithAccount = canManageLayouts && isLayoutAccountShareEnabled
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)

  const { saveAsNew } = useSaveAsNewLayout(dashboardId, getArrangement)
  // A real instance can't reset until its backing layout has loaded — same resolution
  // DashboardModulesRenderer uses to apply the reset, so both sides agree on "ready". [APP-3309]
  const { resetInstanceId, backingLayoutConfig } = useBackingLayoutConfig(dashboardId)
  const isBackingLayoutPending = Boolean(resetInstanceId) && !backingLayoutConfig
  const resettableDashboardId = useMountedContentId("reset", dashboardId)
  let resetUnavailableReason: string | null = null
  if (resettableDashboardId === null) {
    resetUnavailableReason = RESET_NEEDS_PANELS_REASON
  } else if (isBackingLayoutPending) {
    resetUnavailableReason = RESET_LAYOUT_PENDING_REASON
  }
  // "Save as new" reseeds an instance, so it needs a real (UUID) instance to reseed — a "default"/
  // unmigrated-slug dashboard has no instance row for reseedInstance to target. [APP-3309]
  const canSaveAsNew = canManageLayouts && isUuid(dashboardId)

  const openMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget)
  }, [])
  const closeMenu = useCallback(() => { setMenuAnchor(null) }, [])

  const openSaveDialog = useCallback(() => {
    setMenuAnchor(null)
    setIsSaveDialogOpen(true)
  }, [])
  const closeSaveDialog = useCallback(() => { setIsSaveDialogOpen(false) }, [])

  const openResetDialog = useCallback(() => {
    setMenuAnchor(null)
    setIsResetDialogOpen(true)
  }, [])
  const closeResetDialog = useCallback(() => { setIsResetDialogOpen(false) }, [])

  const confirmReset = useCallback(() => {
    dashboardEvents.emit(DASHBOARD_EVENTS.RESET_LAYOUT_REQUESTED, { dashboardId })
    setIsResetDialogOpen(false)
  }, [dashboardId])

  return (
    <>
      <Tooltip title="Layout options">
        <IconButton
          aria-label="Layout actions"
          aria-haspopup="menu"
          size="small"
          color="inherit"
          onClick={openMenu}
        >
          <Tune fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {/* Direct children, not a Fragment: MUI's MenuList walks direct children for keyboard focus. */}
        {canSaveAsNew && (
          <MenuItem onClick={openSaveDialog} disabled={saveAsNewUnavailableReason !== null}>
            <ListItemIcon><SaveAs fontSize="small" /></ListItemIcon>
            <ListItemText primary="Save as new layout" secondary={saveAsNewUnavailableReason} />
          </MenuItem>
        )}
        {canSaveAsNew && <Divider />}
        <MenuItem
          onClick={openResetDialog}
          disabled={resetUnavailableReason !== null}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon><RestartAlt fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primary="Reset layout" secondary={resetUnavailableReason} />
        </MenuItem>
      </Menu>
      {/* Unlike the menu item, not gated on canSaveAsNew — that can withdraw mid-dialog and would otherwise unmount it, dropping the typed name/tier. */}
      <SaveAsNewDialog
        open={isSaveDialogOpen}
        onClose={closeSaveDialog}
        onSave={saveAsNew}
        canShareWithAccount={canShareWithAccount}
      />
      <ResetLayoutConfirmDialog open={isResetDialogOpen} onClose={closeResetDialog} onConfirm={confirmReset} />
    </>
  )
}
